import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MediaContent {
  type: 'image' | 'video' | 'audio' | 'text';
  content: string;
  caption?: string;
  alt?: string;
  autoplay?: boolean;
  loop?: boolean;
  controls?: boolean;
  width?: string | number;
  height?: string | number;
}

interface ContentDisplayProps {
  contents: MediaContent[];
  className?: string;
}

const MediaRenderer: React.FC<MediaContent> = ({
  type,
  content,
  caption,
  alt,
  autoplay = false,
  loop = false,
  controls = true,
  width,
  height,
}) => {
  const commonClassNames = "rounded-lg shadow-md max-w-full m-6";
  
  switch (type) {
    case 'image':
      return (
        <figure className="my-4 flex flex-col items-center"> {/* Added flex container properties */}
          <img
            src={content}
            alt={alt || caption || 'Image'}
            className={`${commonClassNames} object-cover`}
            style={{ width, height }}
          />
          {caption && (
            <figcaption className="text-center text-sm text-gray-600">
              {caption}
            </figcaption>
          )}
        </figure>
      );

      case 'video':
        return (
          <figure className="my-4 flex flex-col items-center">
            <video
              className={commonClassNames}
              controls={controls}
              autoPlay={autoplay}
              loop={loop}
              controlsList="nodownload" // Add this line to remove download option
              style={{ width, height }}
            >
              <source src={content} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            {caption && (
              <figcaption className="text-center text-sm text-gray-600">
                {caption}
              </figcaption>
            )}
          </figure>
        );

    case 'audio':
      return (
        <figure className="my-4">
          <audio
            className={`${commonClassNames} w-full`}
            controls={controls}
            autoPlay={autoplay}
            loop={loop}
          >
            <source src={content} type="audio/mpeg" />
            Your browser does not support the audio tag.
          </audio>
          {caption && (
            <figcaption className="mt-2 text-center text-sm text-gray-600">
              {caption}
            </figcaption>
          )}
        </figure>
      );

      case 'text':
        return (
          <div className="prose prose-sm sm:prose lg:prose-lg xl:prose-xl max-w-none
            prose-headings:font-bold prose-headings:text-gray-900
            prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl
            prose-p:text-gray-600 prose-p:leading-relaxed
            prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
            prose-strong:text-gray-900 prose-strong:font-semibold
            prose-ul:list-disc prose-ul:pl-4
            prose-ol:list-decimal prose-ol:pl-4
            prose-li:text-gray-600
            prose-blockquote:text-gray-600 prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-4
            prose-code:text-gray-800 prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded
            prose-pre:bg-gray-800 prose-pre:text-gray-100 prose-pre:p-4 prose-pre:rounded-lg
            prose-img:rounded-lg prose-img:shadow-md
            prose-hr:border-gray-200">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                // Custom components for specific markdown elements
                h1: ({node, ...props}) => <h1 className="text-3xl font-bold mb-4" {...props}/>,
                h2: ({node, ...props}) => <h2 className="text-2xl font-bold mb-3" {...props}/>,
                h3: ({node, ...props}) => <h3 className="text-xl font-bold mb-2" {...props}/>,
                p: ({node, ...props}) => <p className="mb-4" {...props}/>,
                ul: ({node, ...props}) => <ul className="list-disc pl-5 mb-4" {...props}/>,
                ol: ({node, ...props}) => <ol className="list-decimal pl-5 mb-4" {...props}/>,
                li: ({node, ...props}) => <li className="mb-1" {...props}/>,
                blockquote: ({node, ...props}) => (
                  <blockquote className="border-l-4 border-gray-300 pl-4 italic my-4" {...props}/>
                ),
                code: ({node, inline, ...props}) => 
                  inline ? (
                    <code className="bg-gray-100 rounded px-1 py-0.5" {...props}/>
                  ) : (
                    <code className="block bg-gray-800 text-gray-100 rounded-lg p-4 my-4" {...props}/>
                  ),
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        );

    default:
      return null;
  }
};

export const ContentDisplay: React.FC<ContentDisplayProps> = ({
    contents,
    className = '',
  }) => {
    return (
      <div className={`space-y-6 ${className} bg-gray-100 p-8 rounded-[20px] `}> {/* Add bg color here */}
        {contents.map((content, index) => (
          <div key={index} className="content-item">
            <MediaRenderer {...content} />
          </div>
        ))}
      </div>
    );
  };

// Example usage type for FormWrapper integration:
export interface ContentDisplayField {
  id: string;
  type: 'content-display';
  contents: MediaContent[];
  customComponent?: typeof ContentDisplay;
  page: number;
}